import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  CampaignProduct,
  StepEditorIds,
  StepElements,
  StepTextual,
  CustomStepCategoryEnum,
  WidgetVariable,
  buildPathVariables,
  StepEmulatorDevices,
  StepSectionWidths,
  FunnelInputTypeEnum,
  StepButtons, FunnelInput, User, Funnel,
} from '../../../_models';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Form, FormControlStatus } from '../../../_forms';
import { AlertService, StepEmulatorService, UserService, WidgetService } from '../../../_services';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { addDummyValues, FontFamilyFormats, FontSizes, initializeEditorStyles } from '../../step-util';
import { trigger, state, style, animate, transition } from '@angular/animations';
import * as tinymce from 'tinymce';
import { takeUntil } from 'rxjs/operators';
import {NgxSmartModalService} from 'ngx-smart-modal';
import {FunnelInputFieldsComponent} from '../../../funnel-input';

@Component({
  selector: 'step-cancellation',
  templateUrl: './step-cancellation.component.html',
  styleUrls: ['./step-cancellation.component.scss'],
  animations: [
    trigger('fadeIn', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('500ms ease-in', style({ opacity: 1 }))]),
    ]),
  ],
  encapsulation: ViewEncapsulation.None,
})
export class StepCancellationComponent extends Form implements OnInit, OnDestroy, OnChanges {
  @Input() form: FormGroup;
  @Input() enableFormErrors: boolean;
  @Input() confirmationStepType: string = '';
  @Input() product: CampaignProduct = {} as CampaignProduct;
  @Input() stepType: CustomStepCategoryEnum;
  @Input() activeDevice: StepEmulatorDevices;
  @Input() theme: string;
  @Input() funnel: Funnel;
  stepTextual: StepTextual;
  stepButtons: StepButtons;
  stepEditorIds = StepEditorIds;
  stepElements = StepElements;
  funnelPathVariables: { value: string; text: string }[] = [];
  stepSectionWidths = StepSectionWidths;
  title: string;
  subtitle: string;
  confirmControl: FormGroup;
  rejectControl: FormGroup;
  private subscriptions: Subscription[] = [];
  public selectedInput: FunnelInput = null;
  public user: User = null;
  public selectedInputIndex: number;
  @ViewChild(FunnelInputFieldsComponent, { static: false }) inputComponent: FunnelInputFieldsComponent;

  labelEditor = {
    plugins: 'line-height emoticons link paste autoresize swatch-colors',
    toolbar:
      'bold italic underline strikethrough link | fontsizeselect fontselect lineheightselect| swatch-forecolor swatch-backcolor | ' +
      'alignleft aligncenter alignright | emoticons',
    fontsize_formats: FontSizes,
    font_formats: FontFamilyFormats,
    paste_as_text: true,
    auto_focus: this.stepEditorIds.Label,
    min_height: 120,
    max_height: 200,
    autoresize_bottom_margin: 0,
    setup: (editor: tinymce.Editor) => {
      initializeEditorStyles(editor, '24px', "'Open Sans', Ubuntu, sans-serif", true);

      editor.on('Change KeyUp', () => {
        const content = editor.getContent();
        this.onLabelChange(content);
      });
    },
  };

  contentEditor = {
    plugins: 'lists line-height emoticons link paste autoresize bullist numlist swatch-colors',
    toolbar:
      'bold italic underline strikethrough link | fontsizeselect fontselect lineheightselect | bullist numlist | ' +
      'swatch-forecolor swatch-backcolor | alignleft aligncenter alignright | emoticons',
    fontsize_formats: FontSizes,
    font_formats: FontFamilyFormats,
    paste_as_text: true,
    auto_focus: this.stepEditorIds.Label,
    min_height: 200,
    max_height: 500,
    autoresize_bottom_margin: 0,
    setup: (editor: tinymce.Editor) => {
      initializeEditorStyles(editor, '22px', "'Open Sans', Ubuntu, sans-serif", true);

      editor.on('Change KeyUp', () => {
        const content = editor.getContent();
        this.onContentChange(content);
      });
    },
  };

  buttonEditor = {
    height: 140,
    resize: false,
    plugins: 'line-height emoticons paste autoresize swatch-colors',
    toolbar:
      'bold italic underline strikethrough | fontsizeselect fontselect lineheightselect | swatch-forecolor swatch-backcolor | emoticons',

    fontsize_formats: FontSizes,
    font_formats: FontFamilyFormats,
    paste_as_text: true,
    auto_focus: this.stepEditorIds.ConfirmButton,
    setup: (editor: tinymce.Editor) => {
      initializeEditorStyles(editor, '24px', "'Open Sans', Ubuntu, sans-serif");

      editor.on('Change KeyUp', () => {
        const content = editor.getContent();
        if (editor.id === StepEditorIds.ConfirmButton) {
          this.onConfirmButtonChange(content);
        } else if (editor.id === StepEditorIds.RejectButton) {
          this.onRejectButtonChange(content);
        }
      });
    },
  };

  constructor(
    protected location: Location,
    protected alertService: AlertService,
    protected stepEmulatorService: StepEmulatorService,
    protected router: Router,
    protected widgetService: WidgetService,
    protected userService: UserService,
    protected ngxSmartModalService: NgxSmartModalService
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    const textSubscription = this.stepEmulatorService.stepTextual$.subscribe((data) => {
      this.stepTextual = data;
    });
    this.addSubscription(textSubscription);
    const buttonsSubscription = this.stepEmulatorService.stepButtons$.subscribe((data) => {
      this.stepButtons = data;
    });
    this.addSubscription(buttonsSubscription);
    this.setupButtons();

    if (this.isCancellationConfirmation) {
      this.title = 'Cancellation Confirmation';
      this.subtitle = 'Show a confirmation pop-up before executing the cancel action.';
    } else if (+this.stepType === FunnelInputTypeEnum.Confirmation) {
      this.title = 'Cancellation Response ';
      this.subtitle = 'Customize the final message your customers see after canceling';
    } else {
      this.title = 'Response';
      this.subtitle = 'Customize the message your customers see on this step.';
    }

    this.widgetService
      .getVariables()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (widgets: WidgetVariable[]) => {
          this.funnelPathVariables = buildPathVariables(widgets, {
            supportPhone: this.stepType === CustomStepCategoryEnum.ScheduleSupport,
          });
          this.initalizeValues();
        },
        (error) => {
          this.handleError(error);
        }
      );

    this.userService.getCurrent()
      .subscribe((user: User) => {
        this.user = user;
      }, error => {
        this.handleError(error);
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.product && this.product) {
      this.onLabelChange(this.form.value.label);
      this.onContentChange(this.form.value.content);
    }

    if (changes.theme && this.stepButtons) {
      if (this.theme) {
        this.stepButtons.confirmButton.brandBackground = this.theme;
        this.stepButtons.rejectButton.brandBorder = this.theme;
        this.stepButtons.rejectButton.brandFont = this.theme;
      }
    }
  }

  initalizeValues() {
    this.stepTextual.body.label = addDummyValues(this.form.get('label').value, this.funnelPathVariables, this.product);
    this.stepTextual.body.content = addDummyValues(
      this.form.get('content').value,
      this.funnelPathVariables,
      this.product
    );
  }

  setupButtons() {
    const inputs = this.form.get('inputs') as FormArray;
    this.confirmControl = inputs.at(0) as FormGroup;
    const confirmButton = this.confirmControl.get('label').value;
    this.stepButtons.confirmButton.content = addDummyValues(confirmButton, this.funnelPathVariables, this.product);

    if (this.isCancellationConfirmation) {
      this.rejectControl = inputs.at(1) as FormGroup;
      const rejectButton = this.rejectControl.get('label').value;
      this.stepButtons.rejectButton.content = addDummyValues(rejectButton, this.funnelPathVariables, this.product);
    }

    if (this.theme) {
      this.stepButtons.confirmButton.brandBackground = this.theme;
      this.stepButtons.rejectButton.brandBorder = this.theme;
      this.stepButtons.rejectButton.brandFont = this.theme;
    }
  }

  toggleElement(element: StepElements) {
    this.stepEmulatorService.toggleElement(element);
  }

  isElementVisible(element: StepElements): boolean {
    return this.stepEmulatorService.isElementVisible(element);
  }

  onLabelChange(content: string) {
    if (this.stepTextual) {
      const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product);
      this.stepTextual.body.label = updatedContent;
    }
  }

  onContentChange(content: string) {
    if (this.stepTextual) {
      const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product);
      this.stepTextual.body.content = updatedContent;
    }
  }

  addLabelVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = event.target.value;
    const editorInstance = tinymce.get(this.stepEditorIds.Label);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  addContentVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = event.target.value;

    const editorInstance = tinymce.get(this.stepEditorIds.Content);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  onConfirmButtonChange(content: string) {
    if (this.stepButtons) {
      const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product);
      this.stepButtons.confirmButton.content = updatedContent;
    }
  }

  onRejectButtonChange(content: string) {
    if (this.stepButtons) {
      const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product);
      this.stepButtons.rejectButton.content = updatedContent;
    }
  }

  addConfirmButtonVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    const editorInstance = tinymce.get(this.stepEditorIds.ConfirmButton);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }
    selectElement.selectedIndex = 0;
  }

  addRejectButtonVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    const editorInstance = tinymce.get(this.stepEditorIds.RejectButton);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  get nameInvalidity() {
    return this.enableFormErrors && this.form.controls.name.status === FormControlStatus.Invalid;
  }

  get labelInvalidity() {
    return this.enableFormErrors && this.form.controls.label.status === FormControlStatus.Invalid;
  }

  get contentInvalidity() {
    return this.enableFormErrors && this.form.controls.content.status === FormControlStatus.Invalid;
  }

  get confirmInvalidity() {
    return this.enableFormErrors && this.confirmControl.controls.label.status === FormControlStatus.Invalid;
  }

  get rejectInvalidity() {
    return this.enableFormErrors && this.rejectControl.controls.label.status === FormControlStatus.Invalid;
  }

  get isCancellationConfirmation() {
    return +this.confirmationStepType === FunnelInputTypeEnum.Confirmation;
  }

  addSubscription(subscription: Subscription): void {
    this.subscriptions.push(subscription);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }

  openInputsPopup(index) {
    this.selectedInput = (this.form.get('inputs') as FormArray).at(index).value;
    this.selectedInputIndex = index;
    let _this = this;
    setTimeout(() => {
      _this.ngxSmartModalService.getModal('buttonActions').open();
    })
  }

  onCloseInputsPopup() {
    const input = (this.form.get('inputs') as FormArray).at(this.selectedInputIndex) as FormControl;
    const actions = input.get('actions') as FormArray;
    const data = this.inputComponent.getFormData();
    input.markAsDirty()
    actions.clear();

    data.actions.forEach((action) => {
      actions.push(new FormControl(action));
    });
    this.ngxSmartModalService.getModal('buttonActions').close();
  }
}
